/**
 * One how-to guide.
 *
 * The `honestly` block sits directly under the standfirst, above the answer and
 * far above the steps, and it is rendered in a bordered panel rather than as
 * body text. That placement is the page: three of these five guides answer a
 * question whose premise is false, and a caveat placed after the steps has told
 * the reader the opposite of what it meant to.
 *
 * It is deliberately NOT styled as a warning — no amber, no icon, no
 * "Disclaimer" heading. Anything that looks like small print gets skipped, and
 * this is the part most worth reading.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";

import { HOWTO_SLUGS, howtoIndexPath, howtoPath } from "../../lib/howto/index";
import type { HowtoBundle, HowtoGuide, HowtoSlug } from "../../lib/howto/types";
import type { HowtoUi } from "../../lib/howto/ui";
import { toolPath } from "../../lib/tools/index";
import type { ToolCopy, ToolSlug } from "../../lib/tools/types";
import { ContentFooter } from "../ContentFooter";

export function HowtoPage({
  slug,
  guide,
  ui,
  lang,
  bundle,
  toolCopy,
}: {
  slug: HowtoSlug;
  guide: HowtoGuide;
  ui: HowtoUi;
  lang: ContentLang;
  bundle: HowtoBundle;
  toolCopy: Record<ToolSlug, ToolCopy>;
}) {
  const others = HOWTO_SLUGS.filter((other) => other !== slug);

  return (
    <article className="mx-auto w-full max-w-2xl px-6 py-12 sm:py-16">
      <Link
        to={howtoIndexPath(lang)}
        className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase underline decoration-hair underline-offset-4"
      >
        {ui.backToGuides}
      </Link>

      <h1 className="mt-5 font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {guide.h1}
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-ink2">
        {guide.standfirst}
      </p>

      <section className="brutal mt-8 p-6">
        <h2 className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
          {ui.honestlyHeading}
        </h2>
        <p className="mt-3 text-[17px] leading-[1.7] text-body">
          {guide.honestly}
        </p>
      </section>

      <p className="mt-8 text-[17px] leading-[1.7] text-body">{guide.answer}</p>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.stepsHeading}
        </h2>
        <ol className="mt-5 flex flex-col gap-6">
          {guide.steps.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-pill border-[2.5px] border-ink bg-brand font-display text-sm font-bold text-onbrand"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-ink">
                  {step.title}
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-body">
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.measureHeading}
        </h2>
        <Link
          to={toolPath(guide.tool, lang)}
          className="mt-3 block font-display text-xl font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
        >
          {toolCopy[guide.tool].h1}
        </Link>
        <p className="mt-2 text-[15px] leading-relaxed text-ink2">
          {toolCopy[guide.tool].measures}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.faqHeading}
        </h2>
        {/* Questions as <h3>, not <dt> — same reason as ToolPage: a heading is
            what an extractor splits on and lifts as the question being answered,
            and a <dt> may not contain one. */}
        <div className="mt-4 flex flex-col gap-6">
          {guide.faq.map((item) => (
            <div key={item.q}>
              <h3 className="font-display text-lg font-bold text-ink">
                {item.q}
              </h3>
              <p className="mt-2 text-[17px] leading-[1.7] text-body">
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 border-t-[2.5px] border-ink pt-8">
        <ul className="flex flex-col gap-2">
          {others.map((other) => (
            <li key={other}>
              <Link
                to={howtoPath(other, lang)}
                className="font-display font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
              >
                {bundle[other].h1}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <ContentFooter lang={lang} />
    </article>
  );
}
